import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, RotateCcw, Eye, Smile, Scissors, Sparkles, Star } from "lucide-react";
import { useI18n } from "@/hooks/use-i18n";
import { HeaderBar } from "@/components/HeaderBar";
import { Footer } from "@/components/Footer";
import { ConciergeOrb } from "@/components/ConciergeOrb";
import { AurelianAvatar } from "@/components/AurelianAvatar";
import { ChatStepper } from "@/components/ui/ChatStepper";
import { Card, CardContent } from "@/components/ui/card";
import { testimonials } from "@/data/testimonials";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  trackContactMethodSelected,
  trackContactDetailSubmitted,
  trackConsentGiven,
  trackConsentDeclined,
  trackGoalComplete,
} from "@/lib/uxTracking";
import { getWhatsAppLink } from "@/lib/contactConfig";

type OrbState = "idle" | "listening" | "thinking" | "speaking";

type ContactStep = "method" | "whatsapp" | "phone" | "email" | "consent" | "done";

interface Message {
  role: "user" | "assistant";
  content: string;
  timestamp?: string;
}

interface ReportData {
  summary?: string;
  specialty?: string;
  specialty_label?: string;
  exchange_count?: number;
  slots?: Record<string, unknown>;
}

interface ChatMeta {
  specialty?: string;
  tier?: string;
  score?: number;
}

const MAX_CONTACT_RETRIES = 3;

/** Fallback: détecte dans le texte une fin de rapport si le backend n’envoie pas report_complete. */
function isReportCompleteInReply(reply: string): boolean {
  return /rapport patient|patient report|fin du rapport|end of report|résumé a été enregistré|summary has been recorded|report has been saved|coordinateur examinera|coordinator will review|--- rapport|--- report/i.test(reply);
}

export default function Evaluation() {
  const { t, dir, lang, setLang } = useI18n();
  const isRtl = dir === "rtl";
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [orbState, setOrbState] = useState<OrbState>("idle");
  const [meta, setMeta] = useState<ChatMeta>({});
  const [reportSummary, setReportSummary] = useState<string>("");
  const [reportSpecialty, setReportSpecialty] = useState<string>("");
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [contactStep, setContactStep] = useState<ContactStep | null>(null);
  const [contactMethod, setContactMethod] = useState<"whatsapp" | "phone" | "email" | null>(null);
  const [contactValue, setContactValue] = useState("");
  const [preferredTimeWindow, setPreferredTimeWindow] = useState<"morning" | "afternoon" | "evening" | "">("");
  const [contactRetryCount, setContactRetryCount] = useState(0);
  const [contactError, setContactError] = useState("");
  const [thankYouMessage, setThankYouMessage] = useState("");
  const [savedLeadContactLink, setSavedLeadContactLink] = useState<string | null>(null);
  const [savedLeadContactMethod, setSavedLeadContactMethod] = useState<"whatsapp" | "phone" | "email" | null>(null);
  const [chatStep, setChatStep] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState<{ name: string; url: string }[]>([]);
  const messagesScrollRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    document.title = `${t("evaluation.title")} - Aurelian Private Care`;
  }, [t, lang]);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    messagesEndRef.current?.scrollIntoView({ behavior, block: "end" });
    const container = messagesScrollRef.current;
    if (container) {
      const maxScroll = container.scrollHeight - container.clientHeight;
      container.scrollTo({ top: maxScroll, behavior });
    }
  }, []);

  useEffect(() => {
    scrollToBottom("smooth");
  }, [messages, orbState, scrollToBottom]);

  useEffect(() => {
    if (messages.length === 0) return;
    const el = lastMessageRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => scrollToBottom("auto"));
    observer.observe(el);
    return () => observer.disconnect();
  }, [messages.length, scrollToBottom]);

  const showReportCard = reportData !== null || reportSummary !== "";
  const showContactCard = contactStep !== null && contactStep !== "done";

  useEffect(() => {
    if (showReportCard) setTimeout(() => scrollToBottom("smooth"), 300);
  }, [showReportCard, scrollToBottom]);

  useEffect(() => {
    if (showContactCard) setTimeout(() => scrollToBottom("smooth"), 300);
  }, [showContactCard, scrollToBottom]);

  // Message d'accueil automatique au montage (une seule fois si le chat est vide).
  useEffect(() => {
    if (messages.length > 0) return;
    const welcomeMessages: Record<string, string> = {
      fr: "Bonjour, je suis votre concierge Aurelian. Je suis là pour vous guider vers les meilleurs soins, en toute confidentialité. Choisissez un sujet ci-contre ou décrivez-moi votre besoin.",
      en: "Welcome to Aurelian Private Care. I'm your personal concierge, here to guide you towards the best care in complete confidentiality. Choose a topic or describe your needs.",
      de: "Willkommen bei Aurelian Private Care. Ich bin Ihr persönlicher Concierge. Wählen Sie ein Thema oder beschreiben Sie Ihr Anliegen.",
      es: "Bienvenido a Aurelian Private Care. Soy su concierge personal. Elija un tema o descríbame su necesidad.",
      it: "Benvenuto in Aurelian Private Care. Sono il suo concierge personale. Scelga un argomento o mi descriva la sua esigenza.",
      tr: "Aurelian Private Care'e hoş geldiniz. Kişisel danışmanınızım. Bir konu seçin veya ihtiyacınızı anlatın.",
      ar: "مرحباً بكم في أوريليان برايفت كير. أنا مستشاركم الشخصي. اختاروا موضوعاً أو صفوا لي احتياجكم.",
    };
    const timer = setTimeout(() => {
      setMessages([
        { role: "assistant", content: welcomeMessages[lang] || welcomeMessages.en },
      ]);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  const getSessionId = () => {
    let id = localStorage.getItem("aurelian_session_id");
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem("aurelian_session_id", id);
    }
    return id;
  };

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim()) return;

    const userMsg: Message = { role: "user", content: text.trim() };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setOrbState("thinking");

    try {
      const res = await fetch("/api/aurelian/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: text.trim(),
          language: lang,
          session_id: getSessionId(),
          access_code: localStorage.getItem("aurelian_access_code") || "",
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        setMessages((prev) => [...prev, { role: "assistant", content: t("evaluation.errorMessage") }]);
        setOrbState("idle");
        return;
      }

      setOrbState("speaking");
      setMessages((prev) => [...prev, { role: "assistant", content: data.reply }]);
      setMeta({
        specialty: data.specialty,
        tier: data.tier,
        score: data.score,
      });

      const reportCompleteFromApi = (data as { report_complete?: boolean; report?: ReportData }).report_complete === true;
      const apiReport = (data as { report?: ReportData }).report;
      const summaryFromApi = apiReport?.summary;
      const specialtyFromApi = apiReport?.specialty ?? data.specialty;
      const reportComplete = reportCompleteFromApi || isReportCompleteInReply(data.reply);
      if (reportComplete) {
        setReportSummary(summaryFromApi ?? data.reply);
        setReportSpecialty(specialtyFromApi ?? data.specialty ?? "");
        setReportData(
          apiReport ?? {
            summary: summaryFromApi ?? data.reply,
            specialty: specialtyFromApi ?? data.specialty ?? "",
            specialty_label: apiReport?.specialty_label ?? data.specialty ?? "",
            exchange_count: apiReport?.exchange_count ?? 0,
          }
        );
        setContactStep("method");
        setContactError("");
        setContactRetryCount(0);
      }

      // Progression du stepper (robuste : pas de dépendance à score > 30 ni à report_complete API seul)
      if (data.specialty) setChatStep((s) => (s < 1 ? 1 : s));
      if (data.specialty && typeof data.score === "number") setChatStep((s) => (s < 2 ? 2 : s));
      if (reportComplete) setChatStep((s) => (s < 3 ? 3 : s));

      setTimeout(() => setOrbState("idle"), 1500);
    } catch {
      setMessages((prev) => [...prev, { role: "assistant", content: t("evaluation.errorMessage") }]);
      setOrbState("idle");
    }
  }, [lang, t]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const handleReset = () => {
    setMessages([]);
    setMeta({});
    setOrbState("idle");
    setReportSummary("");
    setReportSpecialty("");
    setReportData(null);
    setContactStep(null);
    setContactMethod(null);
    setContactValue("");
    setPreferredTimeWindow("");
    setContactRetryCount(0);
    setContactError("");
    setThankYouMessage("");
    setSavedLeadContactLink(null);
    setSavedLeadContactMethod(null);
    setChatStep(0);
    setUploadedFiles([]);
    localStorage.removeItem("aurelian_session_id");
  };

  const submitLead = useCallback(
    async (payload: {
      consent_given?: boolean;
      consent_declined?: boolean;
      skipped?: boolean;
      contact_method?: "whatsapp" | "phone" | "email";
      contact_value?: string;
      preferred_time_window?: "morning" | "afternoon" | "evening";
    }) => {
      setContactError("");
      const session_id = getSessionId();
      const anon_user_id = typeof localStorage !== "undefined" ? localStorage.getItem("ux_anon_user_id") ?? undefined : undefined;
      try {
        const res = await fetch("/api/leads", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session_id,
            anon_user_id: anon_user_id || undefined,
            report_summary: reportSummary,
            specialty: reportSpecialty || undefined,
            ...payload,
          }),
        });
        let data: { error?: string; contact_link?: string; contact_method?: string };
        try {
          data = await res.json();
        } catch {
          setContactError(res.ok ? t("evaluation.errorMessage") : t("evaluation.errorMessage"));
          setContactRetryCount((c) => c + 1);
          return false;
        }
        if (!res.ok) {
          const msg =
            data.error === "Invalid email"
              ? t("evaluation.contactInvalidEmail")
              : data.error === "Invalid phone"
                ? t("evaluation.contactInvalidPhone")
                : data.error || t("evaluation.errorMessage");
          setContactError(msg);
          setContactRetryCount((c) => c + 1);
          return false;
        }
        setContactStep("done");
        setChatStep(4);
        const link = typeof (data as { contact_link?: string }).contact_link === "string" ? (data as { contact_link: string }).contact_link : null;
        const method = (data as { contact_method?: string }).contact_method;
        setSavedLeadContactLink(link);
        setSavedLeadContactMethod(method === "whatsapp" || method === "phone" || method === "email" ? method : null);
        if (payload.skipped) setThankYouMessage(t("evaluation.contactThankYouSkipped"));
        else if (payload.consent_declined) setThankYouMessage(t("evaluation.contactThankYouNoConsent"));
        else {
          setThankYouMessage(t("evaluation.contactThankYou"));
          if (payload.consent_given) trackGoalComplete("goal_lead_created");
        }
        return true;
      } catch {
        setContactError(t("evaluation.errorMessage"));
        return false;
      }
    },
    [reportSummary, reportSpecialty, t]
  );

  const handleContactMethod = (method: "whatsapp" | "phone" | "email") => {
    trackContactMethodSelected(method);
    setContactMethod(method);
    setContactStep(method);
    setContactValue("");
    setPreferredTimeWindow("");
    setContactError("");
  };

  const handleContactSkip = () => {
    void submitLead({ skipped: true });
  };

  const handleContactDetailSubmit = () => {
    if (!contactMethod) return;
    trackContactDetailSubmitted(contactMethod);
    const value = contactValue.trim();
    if (!value) {
      setContactError(contactMethod === "email" ? t("evaluation.contactInvalidEmail") : t("evaluation.contactInvalidPhone"));
      return;
    }
    setContactError("");
    setContactStep("consent");
  };

  const handleConsent = (given: boolean) => {
    if (given) trackConsentGiven();
    else trackConsentDeclined();
    if (!given) {
      void submitLead({ consent_declined: true });
      return;
    }
    void submitLead({
      consent_given: true,
      contact_method: contactMethod ?? undefined,
      contact_value: contactValue.trim() || undefined,
      preferred_time_window: contactMethod === "phone" && preferredTimeWindow ? preferredTimeWindow : undefined,
    }).then((ok) => {
      if (!ok && contactRetryCount + 1 >= MAX_CONTACT_RETRIES) {
        setContactStep(contactMethod);
        setContactRetryCount((c) => c + 1);
      }
    });
  };

  const topics = [
    { key: "vision", label: t("evaluation.topicVision"), msg: t("evaluation.topicVisionMsg"), icon: <Eye className="w-4 h-4 text-primary" />, subtitle: "LASIK, PRK, SMILE" },
    { key: "dental", label: t("evaluation.topicDental"), msg: t("evaluation.topicDentalMsg"), icon: <Smile className="w-4 h-4 text-primary" />, subtitle: "Implants, Veneers, All-on-4" },
    { key: "hair", label: t("evaluation.topicHair"), msg: t("evaluation.topicHairMsg"), icon: <Scissors className="w-4 h-4 text-primary" />, subtitle: "FUE, DHI, Sapphire" },
    { key: "aesthetic", label: t("evaluation.topicAesthetic"), msg: t("evaluation.topicAestheticMsg"), icon: <Sparkles className="w-4 h-4 text-primary" />, subtitle: "Rhinoplasty, Facelift, Body" },
  ];

  const orbStatusMap: Record<OrbState, string> = {
    idle: t("evaluation.orbIdle"),
    listening: t("evaluation.orbListening"),
    thinking: t("evaluation.orbThinking"),
    speaking: t("evaluation.orbSpeaking"),
  };

  return (
    <div className="flex flex-col min-h-screen" dir={dir}>
      <HeaderBar lang={lang} onChangeLang={setLang} dir={dir} />

      <main className="flex-1 max-w-6xl mx-auto w-full px-4 md:px-6 py-8">
        <div className="text-center mb-8">
          <h1
            className="text-2xl md:text-3xl font-serif font-bold text-foreground mb-2"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="text-eval-title"
          >
            {t("evaluation.title")}
          </h1>
          <p className="text-sm text-muted-foreground max-w-lg mx-auto" data-testid="text-eval-subtitle">
            {t("evaluation.subtitle")}
          </p>
        </div>

        <div className="max-w-5xl mx-auto px-4 py-6">
          <ChatStepper
            currentStep={chatStep}
            dir={dir}
            stepLabels={[
              t("evaluation.stepperSpecialty"),
              t("evaluation.stepperDetails"),
              t("evaluation.stepperEvaluation"),
              t("evaluation.stepperContact"),
              t("evaluation.stepperConfirmed"),
            ]}
          />

          <div className="mt-6 flex gap-6">
            <div className="flex-1 min-w-0">
              <Card className="border-amber-200/30 bg-white/40 dark:bg-card/60 backdrop-blur-sm shadow-lg overflow-hidden flex flex-col" style={{ minHeight: "calc(100vh - 240px)", maxHeight: "calc(100vh - 240px)" }}>
                <CardContent className="flex-1 flex flex-col p-0 min-h-0">
                  <div
                    ref={messagesScrollRef}
                    className="flex-1 overflow-y-auto overflow-x-hidden px-6 py-6 space-y-4 scroll-smooth"
                    style={{ maxHeight: "calc(100vh - 340px)" }}
                    role="log"
                    aria-live="polite"
                    aria-label="Chat messages"
                  >
                    {messages.length === 0 && (
                      <div className="flex items-center justify-center h-full min-h-[200px]">
                        <p className="text-sm text-muted-foreground/50" data-testid="text-eval-disclaimer">
                          {t("evaluation.disclaimer")}
                        </p>
                      </div>
                    )}

                    {messages.length >= 1 && messages[0].role === "assistant" && (
                      <motion.div
                        className={`flex items-start gap-3 max-w-[90%] ${isRtl ? "flex-row-reverse ml-auto" : ""}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        <AurelianAvatar size="md" />
                        <div className="space-y-3">
                          <div className="bg-white/80 dark:bg-card/80 backdrop-blur-sm border border-amber-200/50 dark:border-border/30 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
                            <p className="text-sm text-foreground leading-relaxed">{messages[0].content}</p>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            {topics.map((topic) => (
                              <motion.button
                                key={topic.key}
                                type="button"
                                onClick={() => sendMessage(topic.msg)}
                                disabled={orbState === "thinking"}
                                whileHover={{ scale: 1.03, y: -2 }}
                                whileTap={{ scale: 0.97 }}
                                className="flex items-center gap-2.5 p-3 rounded-xl bg-white/70 dark:bg-card/60 border border-amber-200/40 dark:border-border/30 hover:border-amber-300 dark:hover:border-primary/30 hover:bg-amber-50/50 dark:hover:bg-primary/5 transition-all text-left shadow-sm disabled:opacity-50"
                                data-testid={`button-topic-${topic.key}`}
                              >
                                <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-primary/10 flex items-center justify-center flex-shrink-0">
                                  {topic.icon}
                                </div>
                                <div className="min-w-0">
                                  <div className="text-sm font-semibold text-amber-900 dark:text-foreground">{topic.label}</div>
                                  <div className="text-xs text-muted-foreground truncate">{topic.subtitle}</div>
                                </div>
                              </motion.button>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    )}

                    <AnimatePresence>
                      {messages.slice(messages.length >= 1 && messages[0].role === "assistant" ? 1 : 0).map((msg, idx) => {
                        const i = idx + (messages.length >= 1 && messages[0].role === "assistant" ? 1 : 0);
                        const isLastMessage = i === messages.length - 1;
                        const isReport =
                          msg.role === "assistant" &&
                          /rapport patient|patient report|--- rapport|--- report|patientenbericht|informe del paciente|rapporto paziente|hasta raporu|تقرير المريض/i.test(msg.content);

                        if (isReport) {
                          const lines = msg.content.split("\n").filter((l) => l.trim());
                          const fields: { label: string; value: string }[] = [];
                          lines.forEach((line) => {
                            const match = line.match(/^\s*(.+?):\s*(.+)$/);
                            if (match && !line.startsWith("---")) {
                              fields.push({ label: match[1].trim(), value: match[2].trim() });
                            }
                          });
                          return (
                            <motion.div
                              key={i}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              className={`flex items-start gap-3 ${isRtl ? "flex-row-reverse" : ""}`}
                              ref={isLastMessage ? lastMessageRef : undefined}
                              data-testid={`message-assistant-${i}`}
                              dir={dir}
                            >
                              <AurelianAvatar size="sm" />
                              <div className="max-w-[85%] space-y-2">
                                <div className="bg-card/80 backdrop-blur-sm border border-primary/20 rounded-xl overflow-hidden shadow-md">
                                  <div className="bg-gradient-to-r from-primary/15 via-primary/10 to-primary/15 px-4 py-2.5 border-b border-primary/10">
                                    <div className="flex items-center gap-2">
                                      <svg className="w-4 h-4 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                        <polyline points="14 2 14 8 20 8" />
                                        <line x1="16" y1="13" x2="8" y2="13" />
                                        <line x1="16" y1="17" x2="8" y2="17" />
                                        <polyline points="10 9 9 9 8 9" />
                                      </svg>
                                      <p className="text-xs font-medium text-primary uppercase tracking-wider">{t("evaluation.reportTitle")}</p>
                                    </div>
                                  </div>
                                  <div className="p-4">
                                    <div className="grid grid-cols-2 gap-3">
                                      {fields.map((field, fi) => (
                                        <div key={fi} className={fields.length % 2 !== 0 && fi === fields.length - 1 ? "col-span-2" : ""}>
                                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{field.label}</p>
                                          <p className="text-sm font-medium text-foreground mt-0.5">{field.value}</p>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                                <div className="bg-card/60 border border-border/30 rounded-xl px-4 py-3">
                                  <div className="flex items-start gap-2">
                                    <svg className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                      <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                    <p className="text-sm text-foreground">{t("evaluation.reportRecorded")}</p>
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          );
                        }

                        return msg.role === "assistant" ? (
                          <motion.div
                            key={i}
                            ref={isLastMessage ? lastMessageRef : undefined}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.3, ease: "easeOut" }}
                            className={`flex items-start gap-3 max-w-[85%] ${isRtl ? "flex-row-reverse ml-auto" : ""}`}
                            data-testid={`message-assistant-${i}`}
                            dir={dir}
                          >
                            <AurelianAvatar size="sm" />
                            <div className="bg-white/80 dark:bg-card/80 backdrop-blur-sm border border-amber-200/50 dark:border-border/30 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
                              <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                            </div>
                          </motion.div>
                        ) : (
                          <motion.div
                            key={i}
                            ref={isLastMessage ? lastMessageRef : undefined}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.3, ease: "easeOut" }}
                            className={`flex items-start gap-3 max-w-[85%] ${isRtl ? "flex-row-reverse" : "ml-auto flex-row-reverse"}`}
                            data-testid={`message-user-${i}`}
                            dir={dir}
                          >
                            <div className="w-8 h-8 rounded-full bg-stone-700 flex items-center justify-center flex-shrink-0">
                              <span className="text-white text-xs font-medium">You</span>
                            </div>
                            <div className="bg-amber-700/90 dark:bg-primary text-white rounded-2xl rounded-tr-sm px-4 py-3 shadow-sm">
                              <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                              {msg.content.startsWith("📎") && uploadedFiles.some((f) => msg.content.includes(f.name)) && (
                                <div className="mt-2 flex flex-wrap gap-2">
                                  {uploadedFiles.filter((f) => msg.content.includes(f.name)).map((f) =>
                                    /\.(jpe?g|png|gif|webp)$/i.test(f.name) ? (
                                      <img key={f.name} src={f.url} alt={f.name} className="max-w-[200px] max-h-[120px] object-cover rounded-lg border border-white/20" />
                                    ) : (
                                      <span key={f.name} className="text-xs text-amber-100 truncate max-w-[200px]">{f.name}</span>
                                    )
                                  )}
                                </div>
                              )}
                            </div>
                          </motion.div>
                        );
                      })}
                    </AnimatePresence>

                    {orbState === "thinking" && (
                      <motion.div
                        className="flex items-start gap-3"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        <AurelianAvatar size="sm" isTyping />
                        <div className="bg-white/80 dark:bg-card/80 backdrop-blur-sm border border-amber-200/50 dark:border-border/30 rounded-2xl rounded-tl-sm px-5 py-3 shadow-sm">
                          <div className="flex gap-1.5">
                            {[0, 1, 2].map((i) => (
                              <motion.div
                                key={i}
                                className="w-2 h-2 rounded-full bg-amber-400 dark:bg-primary"
                                animate={{ y: [0, -6, 0], opacity: [0.4, 1, 0.4] }}
                                transition={{ repeat: Infinity, duration: 0.8, delay: i * 0.15, ease: "easeInOut" }}
                              />
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {showReportCard && (reportData || reportSummary) && (
                      <motion.div
                        className="max-w-md mx-auto"
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                      >
                        <div className="bg-gradient-to-br from-amber-50 to-white dark:from-card dark:to-card/80 border border-amber-300/50 dark:border-primary/20 rounded-2xl overflow-hidden shadow-lg">
                          <div className="bg-gradient-to-r from-amber-700 to-amber-800 dark:from-primary dark:to-primary/90 px-6 py-4">
                            <h3 className="text-white font-semibold text-base flex items-center gap-2">
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              {t("evaluation.reportTitle")}
                            </h3>
                          </div>
                          <div className="px-6 py-4 space-y-3">
                            <div className="flex justify-between items-center py-2 border-b border-amber-100 dark:border-border/30">
                              <span className="text-xs text-muted-foreground uppercase tracking-wider">{t("evaluation.summarySpecialty")}</span>
                              <span className="text-sm font-semibold text-amber-800 dark:text-foreground">{(reportData?.specialty_label ?? reportData?.specialty) || reportSpecialty}</span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b border-amber-100 dark:border-border/30">
                              <span className="text-xs text-muted-foreground uppercase tracking-wider">Exchanges</span>
                              <span className="text-sm font-semibold text-amber-800 dark:text-foreground">{reportData?.exchange_count ?? 0}</span>
                            </div>
                          </div>
                          <div className="px-6 py-3 bg-green-50 dark:bg-green-950/30 border-t border-green-100 dark:border-green-900/30 flex items-center gap-2">
                            <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span className="text-xs text-green-700 dark:text-green-400">{t("evaluation.reportRecorded")}</span>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {contactStep === "done" && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        className="max-w-md mx-auto"
                      >
                        <Card className="border-primary/20 bg-card/80 backdrop-blur-sm overflow-hidden">
                          <div className="bg-gradient-to-r from-primary/15 via-primary/10 to-primary/15 px-4 py-2.5 border-b border-primary/10">
                            <p className="text-[10px] font-medium text-primary uppercase tracking-wider">{t("evaluation.summaryTitle")}</p>
                          </div>
                          <CardContent className="p-4 space-y-3">
                            <div className="grid grid-cols-2 gap-3 text-xs">
                              {meta.specialty && (
                                <div>
                                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("evaluation.summarySpecialty")}</p>
                                  <p className="font-medium text-foreground mt-0.5">{meta.specialty}</p>
                                </div>
                              )}
                              {savedLeadContactMethod && (
                                <div>
                                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("evaluation.summaryContact")}</p>
                                  <p className="font-medium text-foreground mt-0.5 capitalize">{savedLeadContactMethod}</p>
                                </div>
                              )}
                            </div>
                            <div className="flex items-start gap-2.5 pt-2 border-t border-border/20">
                              <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <svg className="w-3 h-3 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-foreground" data-testid="text-contact-thankyou">{thankYouMessage}</p>
                                <p className="text-xs text-muted-foreground mt-1" data-testid="text-next-steps">{t("evaluation.contactNextSteps")}</p>
                              </div>
                            </div>
                            {savedLeadContactMethod === "whatsapp" && savedLeadContactLink && (
                              <a
                                href={savedLeadContactLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors"
                                data-testid="button-whatsapp-now"
                              >
                                {t("evaluation.whatsappNow")}
                              </a>
                            )}
                          </CardContent>
                        </Card>
                      </motion.div>
                    )}

                    {showContactCard && (
                      <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 0.4, ease: "easeOut" }}
                        className="max-w-md mx-auto"
                      >
                        <Card className="bg-card/50 backdrop-blur-xl border-primary/20" data-testid="card-contact-capture">
                          <CardContent className="pt-5 pb-5 px-5 space-y-4">
                            {contactStep === "method" && (
                              <div className="space-y-2">
                                <p className="text-sm font-medium text-foreground" data-testid="text-contact-intro">
                                  {t("evaluation.contactTitle")}
                                </p>
                                {[
                                  { method: "whatsapp" as const, icon: "💬", label: t("evaluation.contactOptionWhatsApp"), color: "hover:border-green-500/30 hover:bg-green-500/5" },
                                  { method: "phone" as const, icon: "📞", label: t("evaluation.contactOptionPhone"), color: "hover:border-blue-500/30 hover:bg-blue-500/5" },
                                  { method: "email" as const, icon: "📧", label: t("evaluation.contactOptionEmail"), color: "hover:border-primary/30 hover:bg-primary/5" },
                                ].map((opt) => (
                                  <button
                                    key={opt.method}
                                    type="button"
                                    onClick={() => handleContactMethod(opt.method)}
                                    className={`w-full flex items-center gap-3 p-3 rounded-xl border border-border/30 transition-all duration-200 ${opt.color}`}
                                    data-testid={`button-contact-${opt.method}`}
                                  >
                                    <span className="text-lg">{opt.icon}</span>
                                    <span className="text-sm font-medium text-foreground">{opt.label}</span>
                                  </button>
                                ))}
                                <Button variant="ghost" size="sm" className="justify-start text-muted-foreground w-full" onClick={handleContactSkip} data-testid="button-contact-skip">
                                  {t("evaluation.contactSkip")}
                                </Button>
                              </div>
                            )}
                            {(contactStep === "whatsapp" || contactStep === "phone" || contactStep === "email") && contactMethod && (
                              <>
                                <p className="text-sm text-muted-foreground" data-testid="text-contact-prompt">
                                  {contactMethod === "whatsapp"
                                    ? t("evaluation.contactPromptWhatsApp")
                                    : contactMethod === "phone"
                                      ? t("evaluation.contactPromptPhone")
                                      : t("evaluation.contactPromptEmail")}
                                </p>
                                <div className="space-y-3">
                                  <Textarea
                                    value={contactValue}
                                    onChange={(e) => setContactValue(e.target.value)}
                                    placeholder={contactMethod === "email" ? t("evaluation.contactPlaceholderEmail") : t("evaluation.contactPlaceholderPhone")}
                                    className="min-h-[44px] resize-none rounded-xl"
                                    data-testid="input-contact-value"
                                  />
                                  <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                      <path d="M7 11V7a5 5 0 0110 0v4" />
                                    </svg>
                                    {t("evaluation.dataEncrypted")}
                                  </div>
                                  {contactMethod === "phone" && (
                                    <div className="flex flex-wrap gap-2">
                                      {(["morning", "afternoon", "evening"] as const).map((tw) => (
                                        <Button
                                          key={tw}
                                          variant={preferredTimeWindow === tw ? "default" : "outline"}
                                          size="sm"
                                          onClick={() => setPreferredTimeWindow(tw)}
                                          data-testid={`button-time-${tw}`}
                                        >
                                          {t(`evaluation.contactTime${tw.charAt(0).toUpperCase() + tw.slice(1)}`)}
                                        </Button>
                                      ))}
                                    </div>
                                  )}
                                  {contactError && (
                                    <p className="text-xs text-destructive" data-testid="text-contact-error">
                                      {contactError}
                                    </p>
                                  )}
                                  <div className="flex gap-2">
                                    <Button size="sm" onClick={handleContactDetailSubmit} data-testid="button-contact-submit-value">
                                      {t("evaluation.contactSubmit")}
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => setContactStep("method")} data-testid="button-contact-back">
                                      {t("evaluation.contactSkip")}
                                    </Button>
                                  </div>
                                </div>
                              </>
                            )}
                            {contactStep === "consent" && (
                              <>
                                <p className="text-sm font-medium text-foreground" data-testid="text-consent-question">
                                  {t("evaluation.contactConsentQuestion")}
                                </p>
                                {contactError && (
                                  <p className="text-xs text-destructive" data-testid="text-contact-error-consent">
                                    {contactError}
                                  </p>
                                )}
                                <div className="flex gap-2">
                                  <Button size="sm" onClick={() => handleConsent(true)} data-testid="button-consent-yes">
                                    {t("evaluation.contactConsentYes")}
                                  </Button>
                                  <Button variant="outline" size="sm" onClick={() => handleConsent(false)} data-testid="button-consent-no">
                                    {t("evaluation.contactConsentNo")}
                                  </Button>
                                </div>
                              </>
                            )}
                          </CardContent>
                        </Card>
                      </motion.div>
                    )}

                    <div ref={messagesEndRef} />
                  </div>

                  <div className="border-t border-amber-200/30 dark:border-border/20 bg-white/60 dark:bg-background/50 backdrop-blur-sm p-4">
                    <div className={`flex items-end gap-3 ${isRtl ? "flex-row-reverse" : ""}`}>
                      <Textarea
                        ref={textareaRef}
                        value={input}
                        onChange={(e) => {
                          setInput(e.target.value);
                          if (orbState === "idle" && e.target.value) setOrbState("listening");
                          if (!e.target.value) setOrbState("idle");
                        }}
                        onKeyDown={handleKeyDown}
                        placeholder={t("evaluation.inputPlaceholder")}
                        className="flex-1 resize-none min-h-[44px] max-h-[120px] border-amber-200/50 dark:border-border/30 bg-white dark:bg-background/50 rounded-xl text-sm placeholder:text-muted-foreground focus:ring-2 focus:ring-amber-300/50 dark:focus:ring-primary/50 focus:border-amber-300 dark:focus:border-primary transition-all shadow-inner disabled:opacity-50"
                        rows={1}
                        data-testid="input-chat-message"
                      />
                      <label className="cursor-pointer w-11 h-11 rounded-xl bg-amber-50 dark:bg-muted border border-amber-200/50 dark:border-border/30 flex items-center justify-center text-amber-600 dark:text-muted-foreground hover:bg-amber-100 dark:hover:bg-muted/80 transition-colors flex-shrink-0">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v13.5A1.5 1.5 0 003.75 21z" />
                        </svg>
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          multiple
                          className="sr-only"
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            files.forEach((file) => {
                              const url = URL.createObjectURL(file);
                              setUploadedFiles((prev) => [...prev, { name: file.name, url }]);
                              setMessages((prev) => [...prev, { role: "user", content: `📎 ${file.name}` }]);
                            });
                            e.target.value = "";
                          }}
                        />
                      </label>
                      <Button
                        size="icon"
                        className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-600 to-amber-700 dark:from-primary dark:to-primary/90 text-white shadow-md hover:from-amber-500 hover:to-amber-600 dark:hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                        onClick={() => sendMessage(input)}
                        disabled={!input.trim() || orbState === "thinking"}
                        data-testid="button-send-message"
                      >
                        <Send className={`w-5 h-5 ${isRtl ? "rotate-180" : ""}`} />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="hidden lg:block w-72 flex-shrink-0 space-y-4 sticky top-24 self-start">
              <Card className="bg-card/50 backdrop-blur-xl border-border/30 shadow-lg">
                <CardContent className="pt-8 pb-6 px-6 flex flex-col items-center">
                  <ConciergeOrb state={orbState} statusText={orbStatusMap[orbState]} />

                  {meta.specialty && (
                    <div className="mt-5 flex flex-wrap gap-2 justify-center">
                      <Badge variant="outline" className="text-xs capitalize" data-testid="badge-specialty">{meta.specialty}</Badge>
                      {meta.tier && meta.tier !== "Initial" && (
                        <Badge variant="secondary" className="text-xs" data-testid="badge-tier">{meta.tier}</Badge>
                      )}
                      {meta.score != null && meta.score > 0 && (
                        <Badge variant="outline" className="text-xs" data-testid="badge-score">Score: {meta.score}</Badge>
                      )}
                    </div>
                  )}

                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-5"
                    onClick={handleReset}
                    data-testid="button-reset-session"
                  >
                    <RotateCcw className={`w-3.5 h-3.5 ${isRtl ? "ml-2" : "mr-2"}`} />
                    {t("evaluation.resetSession")}
                  </Button>
                </CardContent>
              </Card>

              {meta.specialty && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                <Card className="border-border/30 bg-card/60 backdrop-blur-sm">
                  <CardContent className="p-4">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2 font-medium">
                      {t("evaluation.patientTestimonial")}
                    </p>
                    {(() => {
                      const specKey = meta.specialty?.toLowerCase().replace(/\s+/g, "");
                      const match = testimonials.find(
                        (tm) => specKey && (tm.specialty === specKey || specKey.includes(tm.specialty))
                      );
                      if (!match) return null;
                      return (
                        <div className="space-y-2">
                          <div className="flex gap-0.5">
                            {[...Array(match.rating)].map((_, i) => (
                              <Star key={i} className="w-3 h-3 fill-primary text-primary" />
                            ))}
                          </div>
                          <p className="text-xs italic text-foreground/80 leading-relaxed">
                            &quot;{match.quote}&quot;
                          </p>
                          <p className="text-[11px] text-muted-foreground">
                            {match.flag} {match.name}, {match.country}
                          </p>
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>
              </motion.div>
            )}

              <Card className="border-border/30 bg-card/60 backdrop-blur-sm">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground mb-3">{t("evaluation.needHuman")}</p>
                  <div className="flex gap-2">
                    {getWhatsAppLink(t("evaluation.needHumanWhatsAppMessage")) && (
                      <a
                        href={getWhatsAppLink(t("evaluation.needHumanWhatsAppMessage"))}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg bg-green-600 text-white text-xs font-medium hover:bg-green-700 transition-colors"
                      >
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                          <path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492l4.638-1.217A11.95 11.95 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.239 0-4.308-.724-5.993-1.953l-.427-.307-2.75.722.734-2.684-.337-.449A9.955 9.955 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" />
                        </svg>
                        WhatsApp
                      </a>
                    )}
                    <a
                      href="mailto:contact@aurelianprivatecare.com"
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg border border-border/40 text-foreground text-xs font-medium hover:border-primary/30 transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                        <polyline points="22,6 12,13 2,6" />
                      </svg>
                      Email
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      <Footer t={t} dir={dir} />
    </div>
  );
}
